import type { TaskStatus } from '@developerz/domain';
import { A } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { canStopTask, type TaskRow } from '../lib/fleet';
import { taskWorkWord } from '../lib/fleet-work';
import { orgHref } from '../lib/org';
import { ago } from '../lib/time';
import { StatusPill, type StatusTone } from './StatusPill';
import { Button } from './ui/Button';

// One task on the /fleet queue board: the lane chips first (#513) — a live
// "Running" chip driven by `lifecycle_bucket` for in-flight work, a `#N` place
// in line for a queued task (`queue_position`) — then the status pill + title
// on the left, the muted "View run" link on the right — once the runner started
// it (`/fleet/runs/:run_id`, #58). Presenter — the Fleet container hands it a
// resolved queue row (REST backfill or the live `tasks` topic, one shape).

export type TaskQueueItemProps = {
  readonly task: TaskRow;
  /** Open the task's detail drawer (#1034). */
  readonly onOpen: (taskId: string) => void;
  /**
   * Ask to stop this task (#1906). Rendered only where the server would accept
   * it — `canStopTask` mirrors `cancelTask`'s own predicate — so a terminal row
   * shows NO control rather than a disabled one that teaches the button is broken.
   * Absent for a caller who may not stop work (a viewer lacks `write:tasks`,
   * `canWriteTasks`): the row shows no Stop at all.
   */
  readonly onStop?: ((task: TaskRow) => void) | undefined;
};

/** A task status's pill tone; the /fleet summary rows read it too. */
export const TASK_STATUS_TONE: Readonly<Record<TaskStatus, StatusTone>> = {
  pending: 'neutral',
  assigned: 'neutral',
  in_progress: 'live',
  completed: 'success',
  awaiting_ci: 'warning',
  awaiting_review: 'warning',
  mergeable: 'success',
  merged: 'success',
  failed: 'danger',
  cancelled: 'neutral',
  // #6747: accent is for live work, and a paused task is not running.
  paused: 'neutral',
};

// The status pill reads `taskWorkWord` (`lib/fleet-work.ts`): `completed` by its
// outcome (`COMPLETED_LABEL`), every other status by the redesign's work word.

export function TaskQueueItem(props: TaskQueueItemProps): JSX.Element {
  const queuedAt = (): number | null =>
    props.task.lifecycle_bucket === 'queued' ? props.task.queue_position : null;
  return (
    <li
      class="fleet-task"
      data-status={props.task.status}
      data-bucket={props.task.lifecycle_bucket}
    >
      <div class="fleet-task__lead">
        {/* #6747: a paused task buckets `running` (it holds its machine), but nothing runs. */}
        <Show when={props.task.lifecycle_bucket === 'running' && props.task.status !== 'paused'}>
          <StatusPill tone="live">{t('fleet.bucket_running')}</StatusPill>
        </Show>
        <Show when={queuedAt()}>
          {(position) => (
            <StatusPill tone="neutral">
              {t('fleet.task_queue_position', { position: position() })}
            </StatusPill>
          )}
        </Show>
        <StatusPill tone={TASK_STATUS_TONE[props.task.status]}>
          {t(taskWorkWord(props.task))}
        </StatusPill>
        <p class="fleet-task__title">{props.task.title}</p>
        <p class="fleet-task__meta">
          <span>{props.task.repo_full_name}</span>
          <span>{t('fleet.task_created', { when: ago(props.task.created_at) })}</span>
        </p>
      </div>
      <Button variant="ghost" size="sm" onClick={() => props.onOpen(props.task.id)}>
        {t('fleet.task_details')}
      </Button>
      <Show when={props.onStop !== undefined && canStopTask(props.task.status)}>
        <Button
          variant="destructive"
          size="sm"
          aria-label={t('fleet.stop.aria', { title: props.task.title })}
          onClick={() => props.onStop?.(props.task)}
        >
          {t('fleet.stop.action')}
        </Button>
      </Show>
      <Show when={props.task.plan_id}>
        {(planId) => (
          <A class="fleet-task__run" href={orgHref(`/fleet/plans/${planId()}`)}>
            {t('fleet.task_view_plan')}
          </A>
        )}
      </Show>
      <Show when={props.task.run_id}>
        {(runId) => (
          <A class="fleet-task__run" href={orgHref(`/fleet/runs/${runId()}`)}>
            {t('fleet.task_view_run')}
          </A>
        )}
      </Show>
    </li>
  );
}
